(() => {
  const SELECTOR = 'details-modal.lovense-search';

  function initialise(searchModal) {
    if (searchModal.dataset.lovenseSearchReady === 'true') return;
    searchModal.dataset.lovenseSearchReady = 'true';

    const details = searchModal.querySelector('details');
    const input = searchModal.querySelector('input[type="search"]');
    const reset = searchModal.querySelector('button[type="reset"]');
    const typeInput = searchModal.querySelector('[data-lovense-search-type]');
    const tabs = Array.from(searchModal.querySelectorAll('.lovense-search__tab'));

    const closeButton = searchModal.querySelector('.lovense-search__close');
    const overlay = searchModal.querySelector('.lovense-search__overlay');

    let placeholder = null;
    const originalSection = searchModal.closest('.shopify-section');
    const restorePosition = () => {
      if (placeholder?.parentNode) placeholder.replaceWith(searchModal);
      placeholder = null;
      searchModal.classList.remove('lovense-search--portal');
    };
    const moveToViewport = () => {
      if (placeholder || !searchModal.isConnected) return;
      const bounds = searchModal.getBoundingClientRect();
      placeholder = document.createElement('span');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.style.cssText = `display:inline-flex;width:${bounds.width}px;height:${bounds.height}px;flex-shrink:0`;
      searchModal.before(placeholder);
      searchModal.classList.add('lovense-search--portal');
      document.body.append(searchModal);
    };
    if (typeof searchModal.close === 'function') {
      const originalClose = searchModal.close.bind(searchModal);
      searchModal.close = (...args) => {
        restorePosition();
        originalClose(...args);
        document.body.classList.remove('lovense-search-open');
      };
    }
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target !== originalSection) return;
      if (details?.open) closeSearch(false);
      restorePosition();
    });

    const closeSearch = (restoreFocus = true) => {
      if (!details?.open) return;
      if (typeof searchModal.close === 'function') {
        searchModal.close(restoreFocus);
      } else {
        restorePosition();
        details.removeAttribute('open');
        document.body.classList.remove('lovense-search-open', 'overflow-hidden');
      }
    };

    // Keep a guaranteed close path even if predictive-search or another header layer
    // changes stacking/focus behavior.
    closeButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeSearch(true);
    });

    overlay?.addEventListener('click', (event) => {
      event.preventDefault();
      closeSearch(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && details?.open) closeSearch(true);
    });

    let activePanel = 'product';
    const localPanels = Array.from(searchModal.querySelectorAll('[data-search-content]'));
    const predictive = searchModal.querySelector('predictive-search');
    if (predictive) {
      ['onChange', 'onFocus'].forEach((method) => {
        const original = predictive[method].bind(predictive);
        predictive[method] = (...args) => {
          if (activePanel === 'product') return original(...args);
        };
      });
    }
    const faqPanel = searchModal.querySelector('[data-search-content="faq"]');
    const categoryNav = faqPanel?.querySelector('[data-faq-categories]');
    const categoryButtons = [];
    let activeCategory = '';
    if (categoryNav) {
      const categories = new Map();
      faqPanel.querySelectorAll('[data-faq-category]').forEach((item) => {
        const label = item.dataset.faqCategory.trim() || 'General';
        const key = label.toLocaleLowerCase();
        item.dataset.faqCategoryKey = key;
        if (!categories.has(key)) categories.set(key, label);
      });
      categories.forEach((label, key) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lovense-search__faq-category';
        button.textContent = label;
        button.dataset.faqCategoryKey = key;
        categoryNav.append(button);
        categoryButtons.push(button);
        if (!activeCategory) activeCategory = key;
        button.addEventListener('click', () => {
          activeCategory = key;
          filterLocal();
        });
      });
      faqPanel.querySelectorAll('[data-faq-question]').forEach((button) => {
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          button.nextElementSibling.hidden = expanded;
        });
      });
    }
    const filterLocal = () => {
      const query = input.value.trim().toLocaleLowerCase();
      localPanels.forEach((panel) => {
        panel.hidden = panel.dataset.searchContent !== activePanel;
        let count = 0;
        panel.querySelectorAll('[data-search-item]').forEach((item) => {
          const categoryMatches = panel !== faqPanel || !activeCategory || item.dataset.faqCategoryKey === activeCategory;
          item.hidden = !categoryMatches || !item.textContent.toLocaleLowerCase().includes(query);
          if (!item.hidden) count++;
        });
        panel.querySelector('[data-search-empty]').hidden = count > 0;
      });
      categoryButtons.forEach((button) => {
        const selected = button.dataset.faqCategoryKey === activeCategory;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
        if (selected) faqPanel.querySelector('[data-faq-heading]').textContent = button.textContent;
      });
    };
    searchModal.addEventListener('input', (event) => {
      if (event.target === input && activePanel !== 'product') {
        event.stopImmediatePropagation();
        updateQueryState();
      }
    }, true);
    searchModal.addEventListener('focus', (event) => {
      if (event.target === input && activePanel !== 'product') event.stopImmediatePropagation();
    }, true);
    searchModal.addEventListener('submit', (event) => {
      if (activePanel !== 'product') {
        event.preventDefault();
        event.stopImmediatePropagation();
        filterLocal();
      }
    }, true);

    const updateQueryState = () => {
      filterLocal();
      const hasQuery = activePanel === 'product' && Boolean(input && input.value.trim());
      searchModal.classList.toggle('lovense-search--has-query', hasQuery);
      if (reset) reset.classList.toggle('hidden', !input.value.trim());
    };

    if (details) {
      details.addEventListener('toggle', () => {
        const isOpen = details.open;
        if (isOpen) moveToViewport();
        else restorePosition();
        document.body.classList.toggle('lovense-search-open', isOpen);
        if (isOpen) {
          window.requestAnimationFrame(() => input?.focus({ preventScroll: true }));
        }
      });
    }

    input?.addEventListener('input', updateQueryState);
    input?.addEventListener('search', updateQueryState);

    reset?.addEventListener('click', () => {
      window.requestAnimationFrame(() => {
        updateQueryState();
        input?.focus();
      });
    });

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        activePanel = tab.dataset.searchPanel || 'product';
        searchModal.classList.toggle('lovense-search--local', activePanel !== 'product');
        if (predictive) {
          predictive.abortController.abort();
          predictive.abortController = new AbortController();
          predictive.close?.();
        }
        updateQueryState();
        if (activePanel === 'product' && input.value.trim()) predictive?.onChange();
        if (typeInput) typeInput.value = tab.dataset.searchType || 'product';

        if (input) {
          const label = tab.dataset.searchLabel || 'Products';
          input.placeholder = label === 'Products'
            ? input.dataset.defaultPlaceholder
            : `Search ${label}`;
          input.focus();
        }
      });
    });

    updateQueryState();
  }

  const initialiseAll = () => document.querySelectorAll(SELECTOR).forEach(initialise);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', initialiseAll);
})();
